import { CaretDown } from "@phosphor-icons/react";
import { useRouterState } from "@tanstack/react-router";
import { Popover } from "radix-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import { depths } from "~/foundation/styles/depths";
import { NavLauncher } from "~/shell/NavLauncher";
import { navLayerSections, resolveNavSection } from "~/shell/navCatalog";

/**
 * The Console Bar section switcher (layered nav, 2026-09-09). The button names the current
 * section; the panel is the layered launcher — pinned favourites and sections on top, drill
 * into a section for its full list, search across everything. Replaces the capped jump list.
 */
export function ProductPicker() {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [open, setOpen] = useState(false);

  const abilities = workspace?.abilities ?? {};
  const sections = useMemo(() => navLayerSections(abilities), [abilities]);
  const current = resolveNavSection(sections, pathname);
  const CurrentIcon = current?.icon;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t("Switch section")}
          className="flex h-8 items-center gap-1.5 rounded-sm px-2 text-body font-medium text-text-secondary hover:bg-hover-bg hover:text-text data-[state=open]:bg-hover-bg data-[state=open]:text-text"
        >
          {CurrentIcon ? <CurrentIcon size={18} aria-hidden /> : null}
          <span className="hidden md:inline">{current ? t(current.title) : t("Sections")}</span>
          <CaretDown size={12} aria-hidden className="text-text-tertiary" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          // The launcher focuses its own search field; Radix's default would land on the
          // first row instead.
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="slate-pop w-[400px] max-w-[92vw] overflow-hidden rounded-md border border-divider bg-menu-bg shadow-menu"
          style={{ zIndex: depths.menu }}
        >
          <NavLauncher
            variant="popover"
            autoFocus
            onNavigate={() => setOpen(false)}
            onDismiss={() => setOpen(false)}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
